import { Megaphone, Sparkles, Gift, Film } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface Service {
  icon: LucideIcon;
  title: string;
  body: string;
  accent: string;
}

const SERVICES: Service[] = [
  {
    icon: Megaphone,
    title: 'Always-on presence',
    body: 'Light-touch placements that keep your brand visible throughout the experience — never disruptive, never wallpaper.',
    accent: 'from-primary-soft/40 to-transparent',
  },
  {
    icon: Sparkles,
    title: 'Sponsored moments',
    body: 'Take centre stage at the right beat — the moment your audience is most engaged and most likely to remember you.',
    accent: 'from-warning-100/60 to-transparent',
  },
  {
    icon: Film,
    title: 'Story-driven campaigns',
    body: 'Bring your brand story to life with full-screen creative that earns attention rather than steals it.',
    accent: 'from-info-100/60 to-transparent',
  },
  {
    icon: Gift,
    title: 'Reward-driven engagement',
    body: 'Audiences opt in, your brand becomes the reason something good happens. Goodwill that scales with reach.',
    accent: 'from-success-100/60 to-transparent',
  },
];

export function Services() {
  return (
    <section id="services" className="relative">
      <div className="mx-auto max-w-container-lg px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-xs font-semibold uppercase tracking-caps text-primary">
            What we do
          </span>
          <h2 className="mt-3 font-display text-4xl leading-display tracking-display text-ink sm:text-5xl">
            Campaigns that meet your audience{' '}
            <span className="italic text-primary">where they already are.</span>
          </h2>
          <p className="mt-5 text-lg text-ink-secondary sm:text-xl">
            We tailor the shape of every campaign to your brand and your goals —
            so you don&apos;t have to think in ad units.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-5 sm:gap-6 md:grid-cols-2">
          {SERVICES.map(({ icon: Icon, title, body, accent }) => (
            <div
              key={title}
              className="group relative overflow-hidden rounded-lg border border-border-soft bg-surface p-7 shadow-xs transition-shadow duration-base hover:shadow-md sm:p-8"
            >
              <div
                aria-hidden
                className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${accent} opacity-60 transition-opacity duration-base group-hover:opacity-100`}
              />
              <div className="relative">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-md border border-border-soft bg-surface text-primary">
                  <Icon className="h-6 w-6" aria-hidden />
                </span>
                <h3 className="mt-5 font-display text-2xl leading-display text-ink">
                  {title}
                </h3>
                <p className="mt-2.5 text-base leading-body text-ink-secondary">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
